import { useState } from "react";
import type { PersonForm } from "@/components/persons/person-form/types";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";
import { TimeSelect } from "@/components/ui/time-select";

/**
 * Working hours, including the optional split shift.
 *
 * `showSecondShift` lives HERE rather than on the dialog. It used to be
 * lifted to the top of the form purely so the close handler could reset it —
 * but Radix unmounts the dialog's content when it closes, so this state is
 * discarded on close anyway. Keeping it local means one less thing the shell
 * has to remember to undo.
 */
export function WorkingHoursFields({
  form,
  hasSecondShift,
}: {
  form: PersonForm;
  hasSecondShift: boolean;
}) {
  const [showSecondShift, setShowSecondShift] = useState(hasSecondShift);

  return (
    <div className="mt-2 space-y-2">
      <p className="font-medium text-foreground text-sm">
        Working hours <span className="font-normal text-muted-foreground">(Eastern Time, ET)</span>
      </p>
      <div className="flex flex-wrap items-start gap-4">
        <div className="flex w-60 flex-col gap-3">
          <FormField
            control={form.control}
            name="workingHoursStart"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormLabel className="text-xs font-normal text-muted-foreground">Start</FormLabel>
                <TimeSelect
                  value={field.value ?? null}
                  onChange={field.onChange}
                  placeholder="Start"
                  aria-label="Start time (Eastern Time)"
                  aria-invalid={Boolean(fieldState.error)}
                />
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="workingHoursEnd"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormLabel className="text-xs font-normal text-muted-foreground">End</FormLabel>
                <TimeSelect
                  value={field.value ?? null}
                  onChange={field.onChange}
                  placeholder="End"
                  aria-label="End time (Eastern Time)"
                  aria-invalid={Boolean(fieldState.error)}
                />
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <label className="flex cursor-pointer items-center gap-2 self-center text-sm font-medium text-foreground">
          <input
            type="checkbox"
            checked={showSecondShift}
            onChange={(e) => {
              const checked = e.target.checked;
              // Unchecking clears the second block so it's not persisted.
              if (!checked) {
                // shouldDirty so clearing the split shift alone still counts
                // as a change for the review step.
                form.setValue("workingHoursStart2", "", { shouldDirty: true });
                form.setValue("workingHoursEnd2", "", { shouldDirty: true });
              }
              setShowSecondShift(checked);
            }}
            className="h-4 w-4 cursor-pointer rounded border-input text-primary accent-current"
          />
          Split shift
        </label>

        {showSecondShift && (
          <div className="flex w-60 flex-col gap-3">
            <FormField
              control={form.control}
              name="workingHoursStart2"
              render={({ field, fieldState }) => (
                <FormItem>
                  <FormLabel className="text-xs font-normal text-muted-foreground">Start</FormLabel>
                  <TimeSelect
                    value={field.value ?? null}
                    onChange={field.onChange}
                    placeholder="Start"
                    aria-label="Second-set start time (Eastern Time)"
                    aria-invalid={Boolean(fieldState.error)}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="workingHoursEnd2"
              render={({ field, fieldState }) => (
                <FormItem>
                  <FormLabel className="text-xs font-normal text-muted-foreground">End</FormLabel>
                  <TimeSelect
                    value={field.value ?? null}
                    onChange={field.onChange}
                    placeholder="End"
                    aria-label="Second-set end time (Eastern Time)"
                    aria-invalid={Boolean(fieldState.error)}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        )}

        <FormField
          control={form.control}
          name="workingHoursNote"
          render={({ field }) => (
            <FormItem className="min-w-[16rem] flex-1">
              <FormLabel className="text-xs font-normal text-muted-foreground">Note</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="e.g. Mon–Thu, flexible Fridays"
                  className="min-h-[80px]"
                  {...field}
                  value={field.value ?? ""}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </div>
  );
}
